import { io } from 'socket.io-client';
import { basePath } from '../config/constants';
import { formatRelative } from '../utils/formatters';
import { AuthService } from './authservice';
import { authFetch } from './authFetch';

/** Calcula el status del driver basado en timestamp y sesion WebSocket activa */
export function resolveDriverStatus(location, onlineDriverIds) {
  if (!location) return 'offline';
  if (onlineDriverIds && onlineDriverIds.has(location.driverId)) return 'online';

  const elapsed = Date.now() - location.timestamp;
  if (elapsed < 5 * 60 * 1000) return 'online';
  if (elapsed < 15 * 60 * 1000) return 'inactive';
  return 'offline';
}

/**
 * Formatea el tiempo transcurrido de manera legible.
 * Delega en `formatRelative` (fuente única). Se mantiene el nombre exportado
 * `formatElapsed` para no romper sus importadores (TrackingMap/LiveTracking/
 * FleetMap), que pasan un epoch ms.
 * @param {number} timestamp  epoch ms del último reporte.
 * @returns {string}
 */
export function formatElapsed(timestamp) {
  if (!timestamp) return '';
  return formatRelative(timestamp, { invalid: '' });
}

class LocationSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.locationListeners = new Set();
    this.connectionListeners = new Set();
    this.statusListeners = new Set();
    this.onlineDriverIds = new Set(); // drivers con WS activo

    this._baseHttpUrl = null;
  }

  _getBaseUrls() {
    const apiUrl = basePath || process.env.REACT_APP_API_CONNECTION;
    let wsHost, httpBase;
    try {
      const urlObj = new URL(apiUrl);
      const wsProtocol = urlObj.protocol === 'https:' ? 'wss:' : 'ws:';
      wsHost = `${wsProtocol}//${urlObj.host}`;
      httpBase = `${urlObj.protocol}//${urlObj.host}/api/v1`;
    } catch {
      const clean = apiUrl.replace(/\/$/, '');
      wsHost = clean.replace('https://', 'wss://').replace('http://', 'ws://').split('/api/v1')[0];
      httpBase = clean.split('/api/v1')[0] + '/api/v1';
    }
    return { wsHost, httpBase };
  }

  /**
   * Obtiene las ubicaciones via REST (carga inicial).
   * No requiere WebSocket. Funciona aunque el socket este desconectado.
   */
  async fetchLocationsREST(status) {
    const { httpBase } = this._getBaseUrls();
    const url = status
      ? `${httpBase}/locations?status=${status}`
      : `${httpBase}/locations`;

    const headers = { 'Content-Type': 'application/json', 'x-app': 'web' };

    const res = await authFetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`REST /locations error ${res.status}`);
    return res.json(); // LocationBroadcast[]
  }

  /**
   * Obtiene el historial de un driver.
   */
  async fetchDriverHistory(driverId, from, to, limit = 500) {
    const { httpBase } = this._getBaseUrls();
    const params = new URLSearchParams();
    if (from) params.set('from', from instanceof Date ? from.toISOString() : from);
    if (to) params.set('to', to instanceof Date ? to.toISOString() : to);
    params.set('limit', String(limit));

    const url = `${httpBase}/locations/${driverId}/history?${params.toString()}`;
    const headers = { 'Content-Type': 'application/json', 'x-app': 'web' };

    const res = await authFetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`REST historial error ${res.status}`);
    return res.json();
  }

  connect(adminId) {
    if (this.socket?.connected) return;

    const { wsHost } = this._getBaseUrls();
    this._baseHttpUrl = this._getBaseUrls().httpBase;

    this.socket = io(`${wsHost}/location`, {
      path: '/api/v1/socket.io/',
      query: { driverId: adminId, isListener: 'true' },
      // Función: socket.io la evalúa en CADA (re)conexión, así una reconexión tras
      // horas de sesión no reusa un access vencido.
      auth: (cb) => {
        AuthService.getValidAccessToken()
          .then((token) => cb(token ? { token } : {}))
          .catch(() => cb({}));
      },
      forceNew: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    this._setupEventHandlers();
  }

  _setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this._notifyConnectionListeners(true);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this._notifyConnectionListeners(false);
    });

    this.socket.on('connect_error', () => {
      this.isConnected = false;
      this._notifyConnectionListeners(false);
    });

    this.socket.on('location:updated', (data) => {
      if (data?.driverId) {
        this.onlineDriverIds.add(data.driverId);
        this._notifyLocationListeners(data);
      }
    });

    this.socket.on('locations:all', (locations) => {
      if (!Array.isArray(locations)) return;
      locations.forEach((item) => {
        if (item?.driverId && item?.location) {
          if (item.location.status === 'online') this.onlineDriverIds.add(item.driverId);
          this._notifyLocationListeners(item);
        }
      });
    });

    this.socket.on('locations:response', (locations) => {
      if (!Array.isArray(locations)) return;
      locations.forEach((item) => {
        if (item?.driverId && item?.location) {
          this._notifyLocationListeners(item);
        }
      });
    });

    this.socket.on('driver:connected', ({ driverId }) => {
      if (driverId) {
        this.onlineDriverIds.add(driverId);
        this._notifyStatusListeners(driverId, 'online');
      }
    });

    this.socket.on('driver:disconnected', ({ driverId }) => {
      if (driverId) {
        this.onlineDriverIds.delete(driverId);
        this._notifyStatusListeners(driverId, 'offline');
      }
    });
  }

  requestLocations(driverIds) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('No conectado al servidor'));
        return;
      }
      const timeout = setTimeout(() => reject(new Error('Timeout esperando ubicaciones')), 10000);
      this.socket.emit('locations:request', { driverIds });
      this.socket.once('locations:response', (locations) => {
        clearTimeout(timeout);
        resolve(locations);
      });
    });
  }

  onLocationUpdate(callback) {
    this.locationListeners.add(callback);
    return () => this.locationListeners.delete(callback);
  }

  onConnectionChange(callback) {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  onDriverStatusChange(callback) {
    this.statusListeners.add(callback);
    return () => this.statusListeners.delete(callback);
  }

  _notifyLocationListeners(data) {
    this.locationListeners.forEach((fn) => {
      try { fn(data); } catch {}
    });
  }

  _notifyConnectionListeners(connected) {
    this.connectionListeners.forEach((fn) => {
      try { fn(connected); } catch {}
    });
  }

  _notifyStatusListeners(driverId, status) {
    this.statusListeners.forEach((fn) => {
      try { fn(driverId, status); } catch {}
    });
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
    this.isConnected = false;
    this.onlineDriverIds.clear();
    this.locationListeners.clear();
    this.connectionListeners.clear();
    this.statusListeners.clear();
  }

  // Reactiva el socket existente si quedó cortado (p. ej. tras dormir la pestaña) sin
  // crear uno nuevo: connect() usa forceNew y duplicaría sockets y listeners.
  resume() {
    if (this.socket && !this.socket.connected) this.socket.connect();
  }

  getConnectionStatus() {
    return this.isConnected && this.socket?.connected === true;
  }

  getOnlineDriverIds() {
    return new Set(this.onlineDriverIds);
  }
}

export const locationSocketService = new LocationSocketService();
